import type {
    HttpHandler,
    HttpRequest,
    HttpInterceptor,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ActivationStart, Router } from '@angular/router';
import { BuildingAccessRecovery } from '@quipex/core/auth';
import { APP_CONFIG_TOKEN } from '@quipex/core/config';
import {
    BUILDING_PERMISSION_PROBE,
    REQUEST_BUILDING_ID,
    SUPPRESS_ERROR_TOAST,
} from '@quipex/core/primitives';
import { catchError, mergeMap, NEVER, takeUntil } from 'rxjs';
import { HandleErrorService } from '../error';
import { HttpCancelService } from '../http-cancel.service';

@Injectable({
    providedIn: 'root',
})
export class ManageHttpInterceptor implements HttpInterceptor {
    private readonly router = inject(Router);
    private readonly recovery = inject(BuildingAccessRecovery);
    private readonly config = inject(APP_CONFIG_TOKEN);
    private readonly httpCancelService = inject(HttpCancelService);
    private readonly error = inject(HandleErrorService);

    constructor() {
        this.cancelPendingRequestsOnNavigation();
    }

    intercept(req: HttpRequest<unknown>, next: HttpHandler) {
        const buildingId = this.requestBuildingId(req);
        const scope = buildingId
            ? this.recovery.captureBuildingRequest(buildingId)
            : null;
        return next.handle(req).pipe(
            takeUntil(scope?.ended$ ?? NEVER),
            catchError((requestError: unknown) => {
                const suppressAutoErrorToast =
                    req.context.get(SUPPRESS_ERROR_TOAST) === true;
                if (
                    scope &&
                    typeof requestError === 'object' &&
                    requestError !== null &&
                    'status' in requestError &&
                    requestError.status === 403
                ) {
                    return scope.resolveDeniedRequest$().pipe(
                        mergeMap((disposition) =>
                            this.error.handleError(requestError, {
                                suppressAutoErrorToast:
                                    suppressAutoErrorToast ||
                                    disposition !== 'ordinary',
                            })
                        )
                    );
                }
                return this.error.handleError(requestError, {
                    suppressAutoErrorToast,
                });
            })
        );
    }

    private requestBuildingId(req: HttpRequest<unknown>): string | null {
        if (req.context.get(BUILDING_PERMISSION_PROBE)) return null;
        const base = new URL(this.config.apiBase);
        const url = new URL(req.url, base);
        if (url.origin !== base.origin) return null;
        const prefix = `${base.pathname.replace(/\/$/, '')}/api/`;
        if (!url.pathname.startsWith(prefix)) return null;
        const explicit = req.context.get(REQUEST_BUILDING_ID);
        if (explicit) return explicit;
        const segments = url.pathname.slice(prefix.length).split('/');
        return segments[0] === 'buildings' ? (segments[1] ?? null) : null;
    }

    private cancelPendingRequestsOnNavigation() {
        this.router.events.subscribe((event) => {
            if (event instanceof ActivationStart) {
                this.httpCancelService.cancelPendingRequests();
            }
        });
    }
}
